import React from 'react';

import { Grid, IconButton, Tooltip, makeStyles, SvgIcon } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import EditFieldsIcon from '@material-ui/icons/ViewCompact';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { ResizableBox } from 'react-resizable';
import { withResizeDetector } from 'react-resize-detector';

import FormField from './FormField';
import { ReactComponent as ResizableIcon } from '../images/resize.svg';

const useStyles = makeStyles((theme) => ({
    content: {
        marginTop: theme.spacing(6),
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
    resizableBox: {
        wordBreak: 'break-word',
        display: 'flex',
        '&:hover': {
            backgroundColor: 'rgb(189, 189, 189, 0.1)',
        },
        '&:hover $resizableIcon': {
            display: 'block',
            backgroundColor: 'transparent',
        },
        '&:focus-within': {
            backgroundColor: 'rgb(189, 189, 189, 0.1)',
        },
        '&:focus-within $resizableIcon': {
            display: 'block',
        },
    },
    resizableBoxHide: {
        '&:active $resizableIcon': {
            display: 'none',
        },
    },
    resizableIcon: {
        cursor: 'ew-resize',
        height: 100,
        marginTop: 'auto',
        marginBottom: 'auto',
        display: 'none',
        padding: 0,
    },
    field: {
        width: '100%',
        padding: theme.spacing(1),
        '&:hover $buttons': {
            visibility: 'visible',
        },
        '&:focus-within $buttons': {
            visibility: 'visible',
        },
    },
    drag: {
        width: '100%',
        padding: theme.spacing(1),
        backgroundColor: 'rgb(189, 189, 189, 0.1)',
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        visibility: 'hidden',
    },
}));

function DraggableFormDisplay(props) {
    const {
        targetRef,
        width: formWidth,
        fields,
        setFields,
        handleSelectField,
        handleDeleteField,
        handleEditRepeatableField,
    } = props;

    const unitWidth = formWidth / 12;
    const classes = useStyles();

    const onDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination || destination.index === -1) {
            return;
        }
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const field = fields[source.index];
        let updatedFields = Array.from(fields);
        updatedFields.splice(source.index, 1);
        updatedFields.splice(destination.index, 0, field);

        setFields(updatedFields);
    };

    const onResize = (width, index) => {
        const widthInUnits = Math.round((width / formWidth) * 12);
        const field = fields[index];
        const newField = { ...field, width: widthInUnits };
        let updatedFields = Array.from(fields);
        updatedFields.splice(index, 1, newField);

        setFields(updatedFields);
    };

    const decreaseWidth = (index, unitWidth, width) => {
        if (width > 4 || (width > 2 && fields[index].typeId !== 'photoUpload')) {
            const widthInPixel = unitWidth * width - unitWidth;
            onResize(widthInPixel, index);
        }
    };

    const increaseWidth = (index, unitWidth, width) => {
        if (width < 12) {
            const widthInPixel = unitWidth * width + unitWidth;
            onResize(widthInPixel, index);
        }
    };

    const handleResizeKeyDown = (event, index, unitWidth, width) => {
        switch (event.key) {
            case 'ArrowLeft':
                decreaseWidth(index, unitWidth, width);
                break;
            case 'ArrowRight':
                increaseWidth(index, unitWidth, width);
                break;
            case 'ArrowUp':
                onDragEnd({
                    destination: { index: index - 1 },
                    source: { index: index },
                });
                break;
            case 'ArrowDown':
                onDragEnd({
                    destination: { index: index + 1 },
                    source: { index: index },
                });
                break;
            default:
                break;
        }
    };

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className={classes.content}>
                {fields && fields.length > 0 && (
                    <Droppable droppableId={'content'}>
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                <Grid container ref={targetRef}>
                                    {fields.map((field, index) => (
                                        <Grid
                                            container
                                            item
                                            xs={field.width}
                                            md={field.width}
                                            key={`${field.field} ${index}`}
                                        >
                                            <Draggable
                                                key={`${field.field} ${index}`}
                                                draggableId={`${field.field} ${index}`}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <ResizableBox
                                                        className={
                                                            snapshot.isDragging
                                                                ? classes.resizableBoxHide
                                                                : classes.resizableBox
                                                        }
                                                        width={unitWidth * (field.width ? field.width : 12)}
                                                        height={Infinity}
                                                        axis="x"
                                                        minConstraints={[
                                                            field.typeId === 'photoUpload'
                                                                ? unitWidth * 4
                                                                : unitWidth * 2,
                                                            Infinity,
                                                        ]}
                                                        maxConstraints={[formWidth, Infinity]}
                                                        draggableOpts={{
                                                            grid: [unitWidth, unitWidth],
                                                        }}
                                                        onResize={(event, data) => {
                                                            onResize(data.size.width, index);
                                                        }}
                                                        handle={
                                                            <IconButton
                                                                aria-label="Resize"
                                                                className={classes.resizableIcon}
                                                                onKeyDown={(event) => {
                                                                    handleResizeKeyDown(
                                                                        event,
                                                                        index,
                                                                        unitWidth,
                                                                        field.width ? field.width : 12,
                                                                    );
                                                                }}
                                                            >
                                                                <SvgIcon component={ResizableIcon} color="action" />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                snapshot.isDragging ? classes.drag : classes.field
                                                            }
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            ref={provided.innerRef}
                                                        >
                                                            <Grid container item key={field.field} xs={12} md={12}>
                                                                <Grid item xs={12}>
                                                                    <FormField
                                                                        field={field}
                                                                        subEntries={field.fields}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} className={classes.buttons}>
                                                                    {field.typeId === 'multiple' ? (
                                                                        <Tooltip
                                                                            title="Edit Fields"
                                                                            enterDelay={200}
                                                                            disableFocusListener={true}
                                                                        >
                                                                            <IconButton
                                                                                aria-label="Edit Fields"
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    handleEditRepeatableField(
                                                                                        field,
                                                                                        index,
                                                                                    )
                                                                                }
                                                                                className={classes.leftIcon}
                                                                            >
                                                                                <EditFieldsIcon
                                                                                    color="action"
                                                                                    fontSize="small"
                                                                                />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    ) : null}
                                                                    <Tooltip
                                                                        title="Edit"
                                                                        enterDelay={200}
                                                                        disableFocusListener={true}
                                                                    >
                                                                        <IconButton
                                                                            aria-label="Edit"
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleSelectField(field, index)
                                                                            }
                                                                            className={classes.leftIcon}
                                                                        >
                                                                            <EditIcon color="action" fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip
                                                                        title="Delete"
                                                                        enterDelay={200}
                                                                        disableFocusListener={true}
                                                                    >
                                                                        <IconButton
                                                                            aria-label="Delete"
                                                                            size="small"
                                                                            onClick={() => handleDeleteField(field)}
                                                                        >
                                                                            <DeleteIcon
                                                                                color="action"
                                                                                fontSize="small"
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </div>
                                                    </ResizableBox>
                                                )}
                                            </Draggable>
                                        </Grid>
                                    ))}
                                    {provided.placeholder}
                                </Grid>
                            </div>
                        )}
                    </Droppable>
                )}
            </div>
        </DragDropContext>
    );
}

export default withResizeDetector(DraggableFormDisplay);
